// ShiftConflictModal — clean, focused modal shown when a shift creation/update
// overlaps an existing shift, or when a bulk copy skipped shifts due to overlaps.
// Redesigned for calm hierarchy: one big icon, one clear message, compact list.
import { motion, AnimatePresence } from "framer-motion";
import { AlertTriangle, X, Clock, User as UserIcon, Calendar, CheckCircle2 } from "lucide-react";
import { useI18n } from "@/i18n/I18nContext";

/**
 * @param {{
 *  open: boolean,
 *  mode: "overlap" | "partial",
 *  conflicts?: Array<{ shift_id?: string, date?: string, start_time?: string, end_time?: string, employee_name?: string, role_label?: string }>,
 *  skipped?: Array<{ date: string, start_time: string, end_time: string, employee_name?: string, conflict_with?: { start_time: string, end_time: string } }>,
 *  created?: number,
 *  onClose: () => void,
 * }} props
 */
export default function ShiftConflictModal({ open, mode = "overlap", conflicts = [], skipped = [], created = 0, onClose }) {
  const { t } = useI18n();
  const isPartial = mode === "partial";
  const items = isPartial ? skipped : conflicts;
  const accent = isPartial ? "hsl(38 92% 50%)" : "hsl(0 84% 60%)"; // amber for partial, red for overlap
  const accentSoft = isPartial ? "hsl(45 100% 96%)" : "hsl(0 93% 97%)";

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] bg-black/50 backdrop-blur-sm grid place-items-center p-4"
          onClick={onClose}
          data-testid="shift-conflict-modal"
        >
          <motion.div
            initial={{ scale: 0.94, opacity: 0, y: 12 }} animate={{ scale: 1, opacity: 1, y: 0 }} exit={{ scale: 0.94, opacity: 0 }}
            transition={{ duration: 0.2, ease: [0.4, 0, 0.2, 1] }}
            className="card w-full max-w-md p-0 overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            style={{ boxShadow: "var(--shadow-xl)" }}
          >
            {/* Hero header — big icon + title, centered like Apple system dialogs */}
            <div className="relative px-6 pt-8 pb-5 text-center">
              <button
                onClick={onClose}
                data-testid="shift-conflict-close"
                className="absolute top-3 right-3 w-8 h-8 grid place-items-center rounded-full hover:bg-[hsl(var(--muted))] transition-colors"
                aria-label={t("close")}
              >
                <X size={14} strokeWidth={2} />
              </button>
              <div
                className="mx-auto w-14 h-14 rounded-full grid place-items-center mb-3"
                style={{ background: accentSoft, color: accent }}
              >
                <AlertTriangle size={24} strokeWidth={2.2} />
              </div>
              <h3 className="font-display text-[19px] font-bold tracking-tight text-[hsl(var(--text-strong))]">
                {isPartial ? t("copy_partial_title") : t("shift_overlap_title")}
              </h3>
              <p className="mt-1.5 text-[13px] text-[hsl(var(--text-muted))] leading-relaxed max-w-[320px] mx-auto">
                {isPartial ? t("copy_partial_desc") : t("shift_overlap_desc")}
              </p>
            </div>

            {/* Copy-partial stats row — success / skipped as chips */}
            {isPartial && (
              <div className="flex items-center justify-center gap-2 px-6 pb-3">
                <span className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[12px] font-semibold bg-[hsl(var(--success-soft))] text-[hsl(var(--success))]">
                  <CheckCircle2 size={12} strokeWidth={2.4} />
                  {created} {t("copy_partial_created")}
                </span>
                <span className="inline-flex items-center gap-1 h-7 px-3 rounded-full text-[12px] font-semibold" style={{ background: accentSoft, color: accent }}>
                  <AlertTriangle size={11} strokeWidth={2.4} />
                  {items.length} {t("copy_partial_skipped")}
                </span>
              </div>
            )}

            {/* Compact conflict list */}
            <div className="px-6 pb-5">
              <div className="rounded-2xl border hairline overflow-hidden divide-y hairline" data-testid="shift-conflict-list">
                {items.map((c, idx) => (
                  <div
                    key={c.shift_id || `${c.date}-${c.start_time}-${idx}`}
                    className="px-3.5 py-3 flex items-start gap-3 bg-[hsl(var(--surface))]"
                  >
                    {/* Colored rail */}
                    <span aria-hidden className="w-1 self-stretch rounded-full shrink-0" style={{ background: accent, opacity: 0.7 }} />
                    <div className="flex-1 min-w-0">
                      {c.employee_name && (
                        <div className="flex items-center gap-1.5 text-[12.5px] font-semibold text-[hsl(var(--text-strong))] truncate">
                          <UserIcon size={11} strokeWidth={2.2} />
                          <span className="truncate">{c.employee_name}</span>
                        </div>
                      )}
                      <div className="mt-1 flex items-center gap-x-2 gap-y-0.5 flex-wrap text-[12px] tabular-nums text-[hsl(var(--text-muted))]">
                        {c.date && (
                          <span className="inline-flex items-center gap-1">
                            <Calendar size={11} strokeWidth={2} /> {c.date}
                          </span>
                        )}
                        <span className="inline-flex items-center gap-1 font-semibold text-[hsl(var(--text-strong))]">
                          <Clock size={11} strokeWidth={2} /> {c.start_time}–{c.end_time}
                        </span>
                        {!isPartial && c.role_label && (
                          <span className="text-[hsl(var(--text-soft))]">· {c.role_label}</span>
                        )}
                      </div>
                      {isPartial && c.conflict_with && (
                        <div
                          className="mt-1.5 inline-flex items-center gap-1 text-[11px] font-medium px-2 py-0.5 rounded-md"
                          style={{ background: accentSoft, color: accent }}
                        >
                          ↔ {t("existing_shift")} {c.conflict_with.start_time}–{c.conflict_with.end_time}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Footer */}
            <div className="flex items-center justify-end gap-2 px-6 pb-5">
              <button
                onClick={onClose}
                data-testid="shift-conflict-ok"
                className="btn btn-primary flex-1"
              >
                {t("conflict_understood")}
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
